/**
 * Sentry initialisation for the student-facing app.
 *
 * Reporting is gated on `VITE_SENTRY_DSN`, so local dev (where the var is unset)
 * never phones home. Distributed tracing propagates to the backend; privacy is
 * strict - no PII, auth/secrets scrubbed, user reduced to its id - which matters
 * especially here since this app handles student data. Noise-filter decisions
 * live in errorFilters.js so they stay testable.
 */
import * as Sentry from '@sentry/vue';

import { BACKEND_API_URL } from '@/constants';
import { beforeSend, DENY_URLS, IGNORE_ERRORS } from './errorFilters';

/**
 * Initialise Sentry for the given Vue app + router. No-op when no DSN is
 * configured (local dev). Call before `app.mount()`.
 */
export function initSentry(app, router) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  // Trace sample rate is env-tunable per environment (defaults to 0.1). An
  // explicit 0 is honoured so tracing can be disabled without a code change;
  // unset/empty/invalid values fall back to the default.
  const envTracesRate = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE;
  const tracesSampleRate =
    envTracesRate === undefined ||
    envTracesRate === '' ||
    Number.isNaN(Number(envTracesRate))
      ? 0.1
      : Number(envTracesRate);

  Sentry.init({
    app,
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [Sentry.browserTracingIntegration({ router })],
    // Performance tracing; errors are always captured regardless.
    tracesSampleRate,
    // Propagate trace headers to the backend so FE errors link to BE spans.
    tracePropagationTargets: [BACKEND_API_URL],
    // Strict privacy: no IPs/cookies/bodies; keep only the internal user id.
    sendDefaultPii: false,
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
    beforeSend,
  });
}

/**
 * Associate subsequent events with a user, by internal id only (no PII).
 * Safe no-op when Sentry is not initialised (local dev).
 */
export function setSentryUser(user) {
  const id = user?.id ?? null;
  Sentry.setUser(id != null ? { id } : null);
}

/** Clear the user association (call on logout). */
export function clearSentryUser() {
  Sentry.setUser(null);
}
